import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { CheckCircle, Wrench, Users, Crosshair, MapPin, Mail, Phone } from 'lucide-react';

export const AboutSection = () => {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });

  return (
    <section ref={ref} className="py-12 sm:py-24 bg-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: 'repeating-linear-gradient(45deg, #dc2626 0, #dc2626 1px, transparent 0, transparent 50%)'
        }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid md:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Image Placeholder - Mobile optimized height */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="relative order-2 md:order-1"
          >
            <div className="relative rounded-xl sm:rounded-2xl overflow-hidden shadow-xl bg-gray-200">
              {/* Placeholder for Team Image - Responsive height */}
              <div className="w-full h-[300px] sm:h-[450px] lg:h-[550px] flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-300">
                <div className="text-center px-4">
                  <Users className="w-16 h-16 sm:w-20 sm:h-20 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-500 text-lg sm:text-xl font-semibold">MP Frankendach Team</p>
                  <p className="text-gray-400 text-sm mt-1">Platzhalter für Teambild</p>
                </div>
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              <motion.div
                className="absolute bottom-4 left-4 sm:bottom-6 sm:left-6 bg-gradient-to-r from-red-600 to-red-800 text-white px-4 py-2 sm:px-6 sm:py-3 rounded-lg font-bold text-sm sm:text-base"
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                Über 10 Jahre Dachdecker-Erfahrung
              </motion.div>
            </div>
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="order-1 md:order-2"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6">
              Über
              <span className="block bg-gradient-to-r from-red-600 to-red-800 bg-clip-text text-transparent">
                MP Frankendach
              </span>
            </h2>

            <p className="text-gray-700 text-base sm:text-lg mb-4 sm:mb-6 leading-relaxed">
              Ein solides und fachmännisch errichtetes Dach ist unverzichtbar, um Ihr Zuhause vor äußeren Einflüssen zu schützen. Mit über 10 Jahren Erfahrung als Dachdecker bieten wir höchste Qualität.
            </p>

            <p className="text-gray-700 text-base sm:text-lg mb-6 sm:mb-8 leading-relaxed hidden sm:block">
              Wir kombinieren traditionelles Handwerk mit modernen Technologien für optimale Lösungen.
            </p>

            {/* Features - 2x2 Grid */}
            <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-6 sm:mb-8">
              <FeatureCard icon={<CheckCircle />} text="Zuverlässig" />
              <FeatureCard icon={<Wrench />} text="Modern" />
              <FeatureCard icon={<Users />} text="Persönlich" />
              <FeatureCard icon={<Crosshair />} text="Präzise" />
            </div>

            {/* Contact Info - Compact on mobile */}
            <div className="space-y-2 sm:space-y-3">
              <ContactItem 
                icon={<Phone />} 
                label="Telefon" 
                value="0911 - 980 12 850" 
                href="tel:+4991198012850" 
              />
              <ContactItem 
                icon={<Phone />} 
                label="Mobil" 
                value="+49 173 3666776" 
                href="tel:+491733666776" 
              />
              <ContactItem 
                icon={<Mail />} 
                label="E-Mail" 
                value="info@mp-frankendach.de" 
                href="mailto:info@mp-frankendach.de" 
              />
              <ContactItem 
                icon={<MapPin />} 
                label="Standort" 
                value="Schwabacher Str. 260, Fürth" 
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const FeatureCard = ({ icon, text }) => (
  <motion.div
    className="flex items-center space-x-2 sm:space-x-3 p-3 sm:p-4 bg-gradient-to-br from-red-50 to-white rounded-lg border border-red-100"
    whileHover={{ scale: 1.02 }}
  >
    <div className="p-1.5 sm:p-2 bg-gradient-to-br from-red-600 to-red-800 rounded-lg flex-shrink-0">
      {React.cloneElement(icon, { size: 18, className: 'text-white sm:w-5 sm:h-5' })}
    </div>
    <span className="font-semibold text-gray-900 text-sm sm:text-base">{text}</span>
  </motion.div>
);

const ContactItem = ({ icon, label, value, href }) => (
  <motion.div
    className="flex items-center space-x-3 p-3 sm:p-4 bg-gray-50 rounded-lg"
    whileHover={{ x: 3 }}
  >
    {React.cloneElement(icon, { className: 'text-red-600 flex-shrink-0', size: 20 })}
    <div className="min-w-0 flex-1">
      <p className="text-xs sm:text-sm text-gray-500">{label}</p>
      {href ? (
        <a href={href} className="text-sm sm:text-base font-semibold text-gray-900 hover:text-red-600 truncate block">
          {value}
        </a>
      ) : (
        <p className="text-sm sm:text-base font-semibold text-gray-900 truncate">{value}</p>
      )}
    </div>
  </motion.div>
);
